/**
 * Browser-compatible frontmatter parser
 * Replaces gray-matter for browser environments
 */

interface ParsedContent {
  data: Record<string, any>;
  content: string;
}

/**
 * Parse frontmatter from markdown content
 * Supports both --- and +++ delimiters
 */
export function parseFrontmatter(fileContent: string): ParsedContent {
  const lines = fileContent.split('\n');

  // Check if the file starts with frontmatter
  if (!lines[0] || (!lines[0].startsWith('---') && !lines[0].startsWith('+++'))) {
    return {
      data: {},
      content: fileContent
    };
  }

  const delimiter = lines[0].substring(0, 3);
  let endIndex = -1;

  // Find the closing delimiter
  for (let i = 1; i < lines.length; i++) {
    if (lines[i].startsWith(delimiter)) {
      endIndex = i;
      break;
    }
  }

  // If no closing delimiter found, treat entire content as body
  if (endIndex === -1) {
    return {
      data: {},
      content: fileContent
    };
  }

  // Extract frontmatter and content
  const frontmatterLines = lines.slice(1, endIndex);
  const contentLines = lines.slice(endIndex + 1);

  // Parse frontmatter
  const data: Record<string, any> = {};
  let currentKey = '';
  let currentValue: any = '';
  let inArray = false;
  let isMultiline = false;
  let multilineIndent = 0;

  for (let i = 0; i < frontmatterLines.length; i++) {
    const line = frontmatterLines[i];
    const trimmedLine = line.trim();

    // Skip empty lines unless in multiline mode
    if (!trimmedLine && !isMultiline) continue;

    // Handle array items
    if (trimmedLine.startsWith('- ') && currentKey) {
      if (!inArray) {
        data[currentKey] = [];
        inArray = true;
      }
      data[currentKey].push(trimmedLine.substring(2).trim());
      continue;
    }

    // Handle key:value pairs
    const colonIndex = line.indexOf(':');
    if (colonIndex > 0 && !isMultiline) {
      // Save previous key-value if exists
      if (currentKey && !inArray) {
        data[currentKey] = parseValue(currentValue.trim());
      }

      inArray = false;
      isMultiline = false;
      currentKey = line.substring(0, colonIndex).trim();
      currentValue = line.substring(colonIndex + 1).trim();

      // Handle YAML multiline indicators (>, |, >-, |-)
      if (currentValue === '>' || currentValue === '|' ||
        currentValue === '>-' || currentValue === '|-') {
        isMultiline = true;
        currentValue = '';
        // Find the indent level of the next line
        if (i + 1 < frontmatterLines.length) {
          const nextLine = frontmatterLines[i + 1];
          multilineIndent = nextLine.length - nextLine.trimStart().length;
        }
        continue;
      }
      // Handle inline arrays [item1, item2]
      else if (currentValue.startsWith('[') && currentValue.endsWith(']')) {
        data[currentKey] = currentValue
          .substring(1, currentValue.length - 1)
          .split(',')
          .map((item: string) => item.trim())
          .filter((item: string) => item);
        currentKey = '';
        currentValue = '';
      }
      // Handle empty value (array will follow)
      else if (!currentValue) {
        continue;
      }
      // Handle simple key:value
      else {
        data[currentKey] = parseValue(currentValue);
        currentKey = '';
        currentValue = '';
      }
    }
    // Handle multiline values
    else if (currentKey && (isMultiline || (!inArray && currentValue))) {
      if (isMultiline) {
        // Check if this line has less indentation (signals end of multiline)
        const currentIndent = line.length - line.trimStart().length;
        if (trimmedLine && currentIndent < multilineIndent) {
          // This line starts a new key, process it on next iteration
          i--;
          isMultiline = false;
          data[currentKey] = currentValue.trim();
          currentKey = '';
          currentValue = '';
          continue;
        }
        // Add the line to multiline value
        if (currentValue) {
          currentValue += ' ' + line.substring(multilineIndent);
        } else {
          currentValue = line.substring(multilineIndent);
        }
      } else {
        currentValue += '\n' + line;
      }
    }
  }

  // Save last key-value if exists
  if (currentKey) {
    if (inArray) {
      // Arrays are already handled in the loop
    } else if (isMultiline) {
      data[currentKey] = currentValue.trim();
    } else {
      data[currentKey] = parseValue(currentValue.trim());
    }
  }

  return {
    data,
    content: contentLines.join('\n').trim()
  };
}

/**
 * Parse a value string into appropriate type
 */
function parseValue(value: string): any {
  const trimmed = value.trim();

  // Remove quotes if present
  if ((trimmed.startsWith('"') && trimmed.endsWith('"')) ||
    (trimmed.startsWith("'") && trimmed.endsWith("'"))) {
    return trimmed.substring(1, trimmed.length - 1);
  }

  // Boolean
  if (trimmed === 'true') return true;
  if (trimmed === 'false') return false;

  // Null
  if (trimmed === 'null') return null;

  // Number
  if (/^-?\d+(\.\d+)?$/.test(trimmed)) {
    return parseFloat(trimmed);
  }

  // Date (ISO format)
  if (/^\d{4}-\d{2}-\d{2}/.test(trimmed)) {
    return trimmed;
  }

  return trimmed;
}